/**
 * PartnerAuthService - Partner authentication service
 * Handles partner login, signup, OTP verification, and profile management
 */

import apiClient from '../../services/api';
import TokenManager from './TokenManager';

export interface Partner {
  id: number;
  name: string;
  email: string;
  phone: string;
  user_type: string;
  is_partner_verified: boolean;
  business_name?: string;
  business_address?: string;
  gstin?: string;
  pan?: string;
  created_at?: string;
}

export interface AuthResponse {
  success: boolean;
  token: string;
  refreshToken: string;
  expiresIn: number;
  partner: Partner;
  message?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface OTPLoginCredentials {
  phone: string;
  otp: string;
}

export interface SignupData {
  phone: string;
  email: string;
  password: string;
  gym_name: string;
  gym_type: string;
  name?: string;
}

class PartnerAuthService {
  /**
   * Partner signup
   */
  async registerPartner(data: SignupData): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/partners/register', data);
      
      if (response.token && response.refreshToken) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn || 900, // 15 minutes default
          tokenType: 'Bearer'
        });
        
        // Store partner data
        if (response.partner) {
          localStorage.setItem('partner', JSON.stringify(response.partner));
        }
      }
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }

  /**
   * Partner login with email and password
   */
  async loginPartner(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>('/partners/login', credentials);
      
      if (response.token && response.refreshToken) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn || 900,
          tokenType: 'Bearer'
        });
        
        // Store partner data
        if (response.partner) {
          localStorage.setItem('partner', JSON.stringify(response.partner));
        }
      }
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  /**
   * Send OTP to phone for login
   */
  async sendLoginOTP(phone: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/partners/send-login-otp',
        { phone }
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to send OTP');
    }
  }

  /**
   * Verify OTP and login
   */
  async verifyLoginOTP(credentials: OTPLoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post<AuthResponse>(
        '/partners/verify-login-otp',
        credentials
      );
      
      if (response.token && response.refreshToken) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: response.refreshToken,
          expiresIn: response.expiresIn || 900,
          tokenType: 'Bearer'
        });
        
        // Store partner data
        if (response.partner) {
          localStorage.setItem('partner', JSON.stringify(response.partner));
        }
      }
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'OTP verification failed');
    }
  }

  /**
   * Get partner profile
   */
  async getPartnerProfile(): Promise<Partner> {
    try {
      const response = await apiClient.get<Partner>('/partners/profile');
      
      // Update stored partner data
      localStorage.setItem('partner', JSON.stringify(response));
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch profile');
    }
  }

  /**
   * Update partner profile
   */
  async updatePartnerProfile(data: Partial<Partner>): Promise<{ message: string; profile: Partner }> {
    try {
      const response = await apiClient.put<{ message: string; profile: Partner }>(
        '/partners/profile',
        data
      );
      
      // Update stored partner data
      localStorage.setItem('partner', JSON.stringify(response.profile));
      
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update profile');
    }
  }

  /**
   * Verify partner OTP (for 2FA)
   */
  async verifyPartnerOTP(otp: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(
        '/partners/verify-otp',
        { otp }
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'OTP verification failed');
    }
  }

  /**
   * Resend OTP to partner
   */
  async resendPartnerOTP(): Promise<{ message: string; phone: string }> {
    try {
      const response = await apiClient.post<{ message: string; phone: string }>(
        '/partners/resend-otp'
      );
      return response;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to resend OTP');
    }
  }

  /**
   * Refresh access token
   */
  async refreshAccessToken(): Promise<{ token: string; expiresIn: number }> {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.post<{ token: string; expiresIn: number }>(
        '/partners/refresh-token',
        { refreshToken }
      );
      
      if (response.token) {
        TokenManager.setTokens({
          accessToken: response.token,
          refreshToken: refreshToken,
          expiresIn: response.expiresIn || 900,
          tokenType: 'Bearer'
        });
      }
      
      return response;
    } catch (error: any) {
      // Clear tokens if refresh fails
      TokenManager.clearAllTokens();
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  }

  /**
   * Logout partner
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/partners/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear tokens and stored data
      TokenManager.clearAllTokens();
      localStorage.removeItem('partner');
    }
  }

  /**
   * Validate token
   */
  async validateToken(): Promise<{ valid: boolean; partner?: Partner }> {
    try {
      const response = await apiClient.get<{ valid: boolean; partner?: Partner }>(
        '/auth/validate-token'
      );
      return response;
    } catch (error: any) {
      return { valid: false };
    }
  }

  /**
   * Get stored partner data
   */
  getStoredPartner(): Partner | null {
    try {
      const partner = localStorage.getItem('partner');
      return partner ? JSON.parse(partner) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if partner is authenticated
   */
  isAuthenticated(): boolean {
    return TokenManager.hasValidToken();
  }

  /**
   * Check if partner is verified (2FA)
   */
  isPartnerVerified(): boolean {
    const partner = this.getStoredPartner();
    return partner?.is_partner_verified || false;
  }
}

const partnerAuthService = new PartnerAuthService();
export default partnerAuthService;
